import type { ParticipantWidgetLayoutItem, Widget } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import { uuid } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import Chart from '../../../../assets/chart.png';
import Table from '../../../../assets/table.png';
import { FormStack } from '../../../../components/forms/FormStack';
import { showModal } from '../../../../components/generic/Modal';
import { TableWidgetAddModal } from '../table/TableWidgetAddModal';
import { TimeSeriesWidgetAddModal } from '../timeseries/TimeSeriesWidgetAddModal';

interface ParticipantWidgetLayoutItemAddContentProps {
  participantId: string;
  onAdd: (item: ParticipantWidgetLayoutItem) => Promise<void>;
  onClose: () => void;
}

const ParticipantWidgetLayoutItemAddContent = (
  props: ParticipantWidgetLayoutItemAddContentProps,
) => {
  const { participantId, onClose } = props;

  const onAdd = async (widget: Widget) => {
    await props.onAdd({
      id: uuid(),
      widget,
      x: 0,
      y: 0,
      w: 1,
      h: 1,
    });
  };

  const items = [
    {
      name: 'Chart',
      description: 'Display data over time. Useful for frequency, or other time-series data.',
      icon: Chart,
      onClick: () => {
        showModal(TimeSeriesWidgetAddModal, {
          participantId,
          onAdd,
        });
        onClose();
      },
    },
    {
      name: 'Table',
      description: 'Display data in a table. Useful for ABC data, or other tabular data.',
      icon: Table,
      onClick: () => {
        showModal(TableWidgetAddModal, {
          participantId,
          onAdd,
        });
        onClose();
      },
    },
  ];

  return (
    <ChakraV3.Stack bg='white' gap={0} shadow='2xl' rounded='md' overflow='hidden' color='initial'>
      <ChakraV3.HStack px={6} py={3} borderBottomWidth='1px'>
        <ChakraV3.Text fontSize='md' fontWeight='semibold'>
          Add widget
        </ChakraV3.Text>
      </ChakraV3.HStack>
      <FormStack py={4} px={4} orientation='vertical' textAlign='left'>
        <ChakraV3.Stack gap={4}>
          {items.map((item) => (
            <ChakraV3.Button
              key={item.name}
              onClick={item.onClick}
              w='sm'
              h='fit-content'
              rounded='lg'
              py={3}
              px={3}
              variant='ghost'
              borderColor='gray.200'
              borderWidth='1px'
              color='initial'
            >
              <ChakraV3.HStack fontSize='md' w='full' align='center' justify='start' gap={4}>
                <ChakraV3.Image src={item.icon} w='56px' bg='white' p={2} rounded='lg' />
                <ChakraV3.Stack w='full' align='start' gap={0}>
                  <ChakraV3.Text fontWeight='bold'>{item.name}</ChakraV3.Text>
                  <ChakraV3.Text
                    fontSize='sm'
                    fontWeight='normal'
                    textAlign='left'
                    whiteSpace='break-spaces'
                    color='gray.600'
                  >
                    {item.description}
                  </ChakraV3.Text>
                </ChakraV3.Stack>
              </ChakraV3.HStack>
            </ChakraV3.Button>
          ))}
        </ChakraV3.Stack>
      </FormStack>
    </ChakraV3.Stack>
  );
};

interface Props {
  participantId: string;
  onAdd: (item: ParticipantWidgetLayoutItem) => Promise<void>;
  isDisabled?: boolean;
}

export const ParticipantWidgetLayoutItemAddControl = (props: Props) => {
  const { participantId, onAdd, isDisabled } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => (
        <ParticipantWidgetLayoutItemAddContent
          participantId={participantId}
          onAdd={onAdd}
          onClose={onClose}
        />
      )}
    >
      <ChakraV3.Button colorPalette='brand' size='sm' disabled={isDisabled}>
        <FaPlus />
        Add widget
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
